import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
} from 'firebase/firestore';
import DeviceIndicator from './DeviceIndicator';
import './SavedPlaylists.css';

// Text normalization helper function - capitalizes first letter of each word
// Skips normalization for email addresses
const normalizeText = (text) => {
  if (!text) return text;
  // Don't normalize email addresses
  if (text.includes('@')) return text;
  return text
    .split(/\s+/)
    .map((word) => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

function SavedPlaylists({
  playlists,
  onLoadPlaylist,
  onDeletePlaylist,
  onTogglePublic,
  onClose,
  onCreateMasterPlaylist,
  isCreatingMaster,
  user,
}) {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const myPlaylistsRef = useRef(null);
  const [expandedMember, setExpandedMember] = useState(null);
  const [memberPlaylists, setMemberPlaylists] = useState({}); // userId -> playlists array
  const [myPlaylistsExpanded, setMyPlaylistsExpanded] = useState(false); // Start collapsed by default
  const [otherMembersExpanded, setOtherMembersExpanded] = useState(false); // Start collapsed by default
  const [showMasterPlaylistSelector, setShowMasterPlaylistSelector] =
    useState(false);
  const [selectedPlaylistIds, setSelectedPlaylistIds] = useState(new Set());
  const [expandedMembersInSelector, setExpandedMembersInSelector] = useState(
    new Set()
  );
  const [myPlaylistsExpandedInSelector, setMyPlaylistsExpandedInSelector] =
    useState(false); // Collapsed by default
  const [isAppending, setIsAppending] = useState(null); // null = not chosen yet, true = append, false = create new
  const [availablePlaylistsForAppend, setAvailablePlaylistsForAppend] = useState([]);
  const [selectedPlaylistIdForAppend, setSelectedPlaylistIdForAppend] = useState('');
  const [selectedMemberIdForAppend, setSelectedMemberIdForAppend] = useState('');
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [membersForAppend, setMembersForAppend] = useState([]); // Members list that includes current user
  
  // Refs for auto-scrolling
  const masterPlaylistSectionRef = useRef(null);
  const playlistsListRef = useRef(null);
  const panelRef = useRef(null);

  const handleDelete = (playlistId) => {
    if (confirmDelete === playlistId) {
      onDeletePlaylist(playlistId);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(playlistId);
      // Reset after 3 seconds
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  // Load members with public playlists
  useEffect(() => {
    const loadMembers = async () => {
      if (!user) return;

      try {
        setLoadingMembers(true);

        // Query for all public playlists
        const playlistsQuery = query(
          collection(db, 'playlists'),
          where('isPublic', '==', true)
        );

        const playlistsSnapshot = await getDocs(playlistsQuery);

        // Get unique user IDs from public playlists (excluding current user)
        const userIds = new Set();
        playlistsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (
            data.userId &&
            data.isPublic === true &&
            data.userId !== user.uid
          ) {
            userIds.add(data.userId);
          }
        });

        // Fetch user documents for each unique user ID
        const membersData = [];
        for (const userId of userIds) {
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const userPublicPlaylists = playlistsSnapshot.docs.filter(
                (doc) => doc.data().userId === userId
              );

              membersData.push({
                id: userId,
                email: userData.email || '',
                displayName:
                  userData.displayName || userData.email || 'Unknown User',
                photoURL: userData.photoURL || null,
                publicPlaylistCount: userPublicPlaylists.length,
              });
            } else {
              // Fallback: Use email from playlist data
              const userPublicPlaylists = playlistsSnapshot.docs.filter(
                (doc) => doc.data().userId === userId
              );
              if (userPublicPlaylists.length > 0) {
                const firstPlaylist = userPublicPlaylists[0].data();
                const userEmail = firstPlaylist.userEmail || '';
                membersData.push({
                  id: userId,
                  email: userEmail,
                  displayName: userEmail || 'Unknown User',
                  photoURL: null,
                  publicPlaylistCount: userPublicPlaylists.length,
                });
              }
            }
          } catch (err) {
            console.error(`Error loading user ${userId}:`, err);
          }
        }

        membersData.sort((a, b) => a.displayName.localeCompare(b.displayName));
        setMembers(membersData);
      } catch (err) {
        console.error('Error loading members:', err);
      } finally {
        setLoadingMembers(false);
      }
    };

    loadMembers();
  }, [user]);

  // Load members for append mode (includes current user)
  const loadMembersForAppend = async () => {
    if (!user) return;
    
    try {
      const membersList = [];
      
      // Add current user first
      try {
        const currentUserDoc = await getDoc(doc(db, 'users', user.uid));
        const currentUserData = currentUserDoc.exists() ? currentUserDoc.data() : {};
        membersList.push({
          id: user.uid,
          email: user.email || '',
          displayName: currentUserData.displayName || user.email || 'You',
          photoURL: currentUserData.photoURL || null,
        });
      } catch (err) {
        membersList.push({
          id: user.uid,
          email: user.email || '',
          displayName: user.email || 'You',
          photoURL: null,
        });
      }

      // Add other members from existing members list (or query if empty)
      if (members.length > 0) {
        members.forEach(member => {
          if (member.id !== user.uid) {
            membersList.push(member);
          }
        });
      } else {
        // If members list is empty, query for other members with public playlists
        try {
          const playlistsQuery = query(
            collection(db, 'playlists'),
            where('isPublic', '==', true)
          );
          const playlistsSnapshot = await getDocs(playlistsQuery);
          const userIds = new Set();
          playlistsSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.userId && data.userId !== user.uid) {
              userIds.add(data.userId);
            }
          });
          
          for (const userId of userIds) {
            try {
              const userDoc = await getDoc(doc(db, 'users', userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                membersList.push({
                  id: userId,
                  email: userData.email || '',
                  displayName: userData.displayName || userData.email || 'Unknown User',
                  photoURL: userData.photoURL || null,
                });
              }
            } catch (err) {
              console.error(`Error loading user ${userId}:`, err);
            }
          }
        } catch (err) {
          console.error('Error loading other members:', err);
        }
      }

      // Update members state if it was empty
      if (members.length === 0 && membersList.length > 1) {
        setMembers(membersList.filter(m => m.id !== user.uid));
      }

      // Store members list that includes current user for the dropdown
      setMembersForAppend(membersList);

      // Auto-select current user by default
      if (membersList.length > 0) {
        setSelectedMemberIdForAppend(user.uid);
        await loadPlaylistsForAppend(user.uid);
      }
    } catch (error) {
      console.error('❌ Error loading members for append:', error);
    }
  };

  // Load playlists for selected member in append mode
  const loadPlaylistsForAppend = async (memberId) => {
    if (!user || !memberId) return;
    
    try {
      let q;
      if (memberId === user.uid) {
        q = query(
          collection(db, 'playlists'),
          where('userId', '==', memberId)
        );
      } else {
        q = query(
          collection(db, 'playlists'),
          where('userId', '==', memberId),
          where('isPublic', '==', true)
        );
      }
      
      const querySnapshot = await getDocs(q);
      const playlists = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        playlists.push({ 
          id: doc.id, 
          name: data.name || 'Unnamed Playlist',
          trackCount: data.trackCount || 0,
          source: data.source || 'local',
          tracks: data.tracks || [],
          ...data 
        });
      });

      playlists.sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setAvailablePlaylistsForAppend(playlists);
    } catch (error) {
      console.error('❌ Error loading playlists for append:', error);
      setAvailablePlaylistsForAppend([]);
    }
  };

  // Load playlists for a specific member when their accordion is expanded
  const handleMemberToggle = async (memberId) => {
    if (expandedMember === memberId) {
      setExpandedMember(null);
      return;
    }

    setExpandedMember(memberId);

    // Auto-scroll to hide green button and maximize space for member playlists
    // Only on mobile landscape (limited viewport)
    if (window.innerWidth <= 767 && window.innerWidth > window.innerHeight) {
      // Use multiple animation frames and timeout to ensure DOM is fully updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (panelRef.current && masterPlaylistSectionRef.current) {
              // Find the expanded member accordion header
              const accordionHeader = panelRef.current.querySelector(
                `.member-accordion-header[data-member-id="${memberId}"].expanded, .member-accordion-header[data-expanded="true"][data-member-id="${memberId}"]`
              );
              
              if (accordionHeader) {
                // Get positions relative to panel
                const panelRect = panelRef.current.getBoundingClientRect();
                const headerRect = accordionHeader.getBoundingClientRect();
                const buttonRect = masterPlaylistSectionRef.current.getBoundingClientRect();
                
                // Calculate scroll position: scroll until accordion header is at top, hiding button
                const scrollPosition = headerRect.top - panelRect.top + panelRef.current.scrollTop - 10;
                
                console.log('Auto-scroll debug:', {
                  memberId,
                  panelScrollTop: panelRef.current.scrollTop,
                  scrollPosition,
                  panelScrollHeight: panelRef.current.scrollHeight,
                  panelClientHeight: panelRef.current.clientHeight,
                  buttonHeight: buttonRect.height,
                  headerTop: headerRect.top - panelRect.top
                });
                
                panelRef.current.scrollTo({
                  top: Math.max(0, scrollPosition), // Ensure non-negative
                  behavior: 'smooth'
                });
              } else {
                // Fallback: scroll by button height
                const header = panelRef.current.querySelector('.saved-playlists-header');
                const headerHeight = header ? header.offsetHeight : 0;
                const buttonSectionHeight = masterPlaylistSectionRef.current.offsetHeight;
                const scrollPosition = headerHeight + buttonSectionHeight + 10;
                
                console.log('Auto-scroll fallback:', {
                  memberId,
                  headerHeight,
                  buttonSectionHeight,
                  scrollPosition
                });
                
                panelRef.current.scrollTo({
                  top: scrollPosition,
                  behavior: 'smooth'
                });
              }
            } else {
              console.log('Auto-scroll failed: refs not available', {
                hasPanelRef: !!panelRef.current,
                hasButtonRef: !!masterPlaylistSectionRef.current
              });
            }
          }, 800); // Wait for accordion expansion animation to complete
        });
      });
    }

    // If we already loaded playlists for this member, don't reload
    if (memberPlaylists[memberId]) {
      return;
    }

    try {
      const playlistsQuery = query(
        collection(db, 'playlists'),
        where('userId', '==', memberId),
        where('isPublic', '==', true)
      );

      const playlistsSnapshot = await getDocs(playlistsQuery);
      const playlistsData = playlistsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by updatedAt in JavaScript (descending - most recent first)
      playlistsData.sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setMemberPlaylists((prev) => ({
        ...prev,
        [memberId]: playlistsData,
      }));
    } catch (err) {
      console.error('Error loading member playlists:', err);
    }
  };

  // Load all member playlists when selector opens
  useEffect(() => {
    if (showMasterPlaylistSelector && members.length > 0) {
      const loadAllMemberPlaylists = async () => {
        for (const member of members) {
          if (!memberPlaylists[member.id]) {
            try {
              const playlistsQuery = query(
                collection(db, 'playlists'),
                where('userId', '==', member.id),
                where('isPublic', '==', true)
              );
              const playlistsSnapshot = await getDocs(playlistsQuery);
              const playlistsData = playlistsSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              playlistsData.sort((a, b) => {
                const aTime = new Date(
                  a.updatedAt || a.createdAt || 0
                ).getTime();
                const bTime = new Date(
                  b.updatedAt || b.createdAt || 0
                ).getTime();
                return bTime - aTime;
              });
              setMemberPlaylists((prev) => ({
                ...prev,
                [member.id]: playlistsData,
              }));
            } catch (err) {
              console.error('Error loading member playlists:', err);
            }
          }
        }
      };
      loadAllMemberPlaylists();
    }
  }, [showMasterPlaylistSelector, members, memberPlaylists]);

  return (
      <div className="saved-playlists-overlay">
      <div ref={panelRef} className="saved-playlists-panel">
        <div className="saved-playlists-header">
          <h2>Available Playlists</h2>
          <DeviceIndicator inline={true} />
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div
          ref={masterPlaylistSectionRef}
          className="master-playlist-section"
          style={{
            padding: '10px 1rem',
            borderBottom: '1px solid transparent',
            marginBottom: '0',
            marginTop: '0',
          }}
        >
          <button
            className="btn-create-master"
            onClick={() => setShowMasterPlaylistSelector(true)}
            disabled={isCreatingMaster}
            style={{
              width: '100%',
              padding: '10px 0.75rem',
              marginTop: '0',
              marginBottom: '0',
              background: isCreatingMaster
                ? '#555'
                : 'linear-gradient(135deg, #1db954 0%, #1ed760 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: isCreatingMaster ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: isCreatingMaster
                ? 'none'
                : '0 2px 10px rgba(29, 185, 84, 0.3)',
            }}
          >
            {isCreatingMaster
              ? '⏳ Creating Playlist...'
              : '🎲 Create/Append Playlist'}
          </button>
          <p
            style={{
              marginTop: '0.5rem',
              fontSize: '0.85rem',
              color: '#aaa',
              textAlign: 'center',
            }}
          >
            Select playlists to combine into one shuffled playlist
          </p>
        </div>

        <div ref={playlistsListRef} className="playlists-list">
          {/* My Playlists Accordion Section */}
          <div ref={myPlaylistsRef} className="my-playlists-accordion">
            <button
              className={`my-playlists-accordion-header ${
                myPlaylistsExpanded ? 'expanded' : ''
              }`}
              onClick={() => {
                setMyPlaylistsExpanded(!myPlaylistsExpanded);
                if (!myPlaylistsExpanded) {
                  setOtherMembersExpanded(false);
                }
              }}
            >
              <div className="section-header-info">
                <div className="section-header-title">📋 My Playlists</div>
                <div className="section-header-count">{playlists.length}</div>
              </div>
              <span className="accordion-icon">
                {myPlaylistsExpanded ? '▼' : '▶'}
              </span>
            </button>

            {myPlaylistsExpanded && (
              <div className="my-playlists-accordion-content">
                {playlists.length === 0 ? (
                  <div className="playlists-empty">
                    <div className="playlists-empty-icon">🎵</div>
                    <p>No saved playlists yet</p>
                    <p className="playlists-empty-hint">
                      Use Shuffle + Swipe mode to create your first playlist!
                    </p>
                  </div>
                ) : (
                  playlists.map((playlist) => (
                    <div key={playlist.id} className="playlist-card">
                      <div className="playlist-info">
                        <h3 className="playlist-name">
                          {normalizeText(playlist.name)}
                          {playlist.trackCount !== undefined && playlist.trackCount !== null && (
                            <span className="playlist-count-inline"> {playlist.trackCount || 0}</span>
                          )}
                        </h3>
                        {/* Description hidden per user request */}
                        <div className="playlist-meta">
                          {playlist.trackCount !== undefined && playlist.trackCount !== null && (
                            <>
                              <span className="playlist-tracks">
                                🎵 {playlist.trackCount || 0} songs
                              </span>
                              <span className="playlist-meta-separator">|</span>
                            </>
                          )}
                          <span className="playlist-date">
                            {new Date(playlist.createdAt).toLocaleDateString()}
                          </span>
                          {playlist.isPublic && (
                            <>
                              <span className="playlist-meta-separator">|</span>
                              <span
                                className="playlist-public-badge"
                                title="This playlist is public"
                              >
                                🌐 Public
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="playlist-actions">
                        <button
                          className="btn-play-playlist"
                          onClick={() => onLoadPlaylist(playlist)}
                        >
                          ▶ Play
                        </button>
                        {onTogglePublic && (
                          <button
                            className={`btn-toggle-public ${
                              playlist.isPublic ? 'public' : 'private'
                            }`}
                            onClick={() =>
                              onTogglePublic(
                                playlist.id,
                                playlist.isPublic || false
                              )
                            }
                            title={
                              playlist.isPublic ? 'Make private' : 'Make public'
                            }
                          >
                            {playlist.isPublic ? '🔒' : '🌐'}
                          </button>
                        )}
                        <button
                          className={`btn-delete-playlist ${
                            confirmDelete === playlist.id ? 'confirm' : ''
                          }`}
                          onClick={() => handleDelete(playlist.id)}
                        >
                          {confirmDelete === playlist.id ? '✓ Confirm' : '🗑️'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Other Members' Playlists Accordion Section */}
          {members.length > 0 && (
            <div className="members-playlists-section">
              <div className="other-members-accordion">
                <button
                  className={`other-members-accordion-header ${
                    otherMembersExpanded ? 'expanded' : ''
                  }`}
                  onClick={() => {
                    setOtherMembersExpanded(!otherMembersExpanded);
                    if (!otherMembersExpanded) {
                      setMyPlaylistsExpanded(false);
                    }
                  }}
                >
                  <div className="section-header-info">
                    <div className="section-header-title">👥 Other Members' Playlists</div>
                    <div className="section-header-count">{members.length}</div>
                  </div>
                  <span className="accordion-icon">
                    {otherMembersExpanded ? '▼' : '▶'}
                  </span>
                </button>

                {otherMembersExpanded && (
                  <div className="other-members-accordion-content">
                    {members.map((member) => (
                <div key={member.id} className="member-accordion">
                  <button
                    className={`member-accordion-header ${
                      expandedMember === member.id ? 'expanded' : ''
                    }`}
                    data-expanded={expandedMember === member.id}
                    data-member-id={member.id}
                    onClick={() => handleMemberToggle(member.id)}
                  >
                    <div className="member-header-info">
                      <div className="member-name">{normalizeText(member.displayName)}</div>
                      <span className="member-playlist-count">
                        {member.publicPlaylistCount}
                      </span>
                    </div>
                    <span className="accordion-icon">
                      {expandedMember === member.id ? '▼' : '▶'}
                    </span>
                  </button>

                  {expandedMember === member.id && (
                    <div className="member-accordion-content">
                      {memberPlaylists[member.id] ? (
                        memberPlaylists[member.id].length > 0 ? (
                          memberPlaylists[member.id].map((playlist) => (
                            <div
                              key={playlist.id}
                              className="playlist-card member-playlist-card"
                            >
                              <div className="playlist-info">
                                <h3 className="playlist-name">
                                  {normalizeText(playlist.name)}
                                  {playlist.trackCount !== undefined && playlist.trackCount !== null && (
                                    <span className="playlist-count-inline"> {playlist.trackCount || 0}</span>
                                  )}
                                </h3>
                                {/* Description hidden per user request */}
                                <div className="playlist-meta">
                                  {playlist.trackCount !== undefined && playlist.trackCount !== null && (
                                    <>
                                      <span className="playlist-tracks">
                                        🎵 {playlist.trackCount || 0} songs
                                      </span>
                                      <span className="playlist-meta-separator">|</span>
                                    </>
                                  )}
                                  <span className="playlist-date">
                                    {new Date(
                                      playlist.updatedAt || playlist.createdAt
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                              <div className="playlist-actions">
                                <button
                                  className="btn-play-playlist member-play-btn"
                                  onClick={() => {
                                    onLoadPlaylist(playlist);
                                    // Scroll My Playlists to top when member playlist is opened
                                    setTimeout(() => {
                                      if (myPlaylistsRef.current && panelRef.current) {
                                        // The panel is the scrollable container
                                        const panel = panelRef.current;
                                        const accordion = myPlaylistsRef.current;
                                        
                                        // Calculate position relative to panel
                                        const panelRect = panel.getBoundingClientRect();
                                        const accordionRect = accordion.getBoundingClientRect();
                                        const scrollTop = panel.scrollTop;
                                        const relativeTop = accordionRect.top - panelRect.top + scrollTop;
                                        
                                        panel.scrollTo({
                                          top: relativeTop - 10, // 10px offset from top
                                          behavior: 'smooth'
                                        });
                                      }
                                    }, 200);
                                  }}
                                >
                                  ▶
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="playlists-empty-small">
                            <p>No public playlists available</p>
                          </div>
                        )
                      ) : (
                        <div className="playlists-loading-small">
                          <p>Loading playlists...</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Master Playlist Selection Modal */}
      {showMasterPlaylistSelector && (
        <div 
          className="master-playlist-selector-overlay"
          onClick={(e) => {
            // Allow closing by clicking overlay - user can cancel anytime
            if (e.target === e.currentTarget) {
              setShowMasterPlaylistSelector(false);
              setSelectedPlaylistIds(new Set());
              setIsAppending(null);
              setSelectedPlaylistIdForAppend('');
              setSelectedMemberIdForAppend('');
              setNewPlaylistName('');
            }
          }}
        >
          <div className="master-playlist-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="master-playlist-selector-header">
              <h2>Select Playlists for Playlist</h2>
              <button
                className="close-btn"
                onClick={() => {
                  // Allow closing - user can cancel anytime
                  setShowMasterPlaylistSelector(false);
                  setSelectedPlaylistIds(new Set());
                  setIsAppending(null);
                  setSelectedPlaylistIdForAppend('');
                  setSelectedMemberIdForAppend('');
                  setNewPlaylistName('');
                }}
              >
                ✕
              </button>
            </div>

            {/* Step 1: Select Destination Playlist (Create New or Append to Existing) */}
            <div style={{ padding: '1rem', borderBottom: '1px solid #333', backgroundColor: '#1a1a1a' }}>
              <h3 style={{ marginBottom: '15px', color: '#fff', fontSize: '1.1rem' }}>Step 1: Select Destination Playlist</h3>
              <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setIsAppending(false);
                    setSelectedPlaylistIdForAppend('');
                    setSelectedMemberIdForAppend('');
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '16px',
                    backgroundColor: isAppending === false ? '#667eea' : '#ccc',
                    color: isAppending === false ? 'white' : 'black',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  Create New
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAppending(true);
                    // Don't clear selectedPlaylistIds when switching modes
                    if (user) {
                      loadMembersForAppend();
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '16px',
                    backgroundColor: isAppending === true ? '#667eea' : '#ccc',
                    color: isAppending === true ? 'white' : 'black',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  Append to Existing
                </button>
              </div>

              {/* Create New: Show name input - Only when Create New is selected */}
              {isAppending === false && (
                <div style={{ marginTop: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#b3b3b3', fontSize: '0.9rem' }}>
                    Playlist Name * <span style={{ color: '#ff6b6b' }}>(Required)</span>
                  </label>
                  <input
                    type="text"
                    value={newPlaylistName}
                    onChange={(e) => setNewPlaylistName(e.target.value)}
                    placeholder="Enter playlist name (required)"
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      backgroundColor: '#2a2a2a',
                      color: '#fff',
                      border: newPlaylistName.trim() ? '1px solid #555' : '1px solid #ff6b6b',
                      borderRadius: '4px'
                    }}
                  />
                  {!newPlaylistName.trim() && (
                    <div style={{ marginTop: '5px', color: '#ff6b6b', fontSize: '0.85rem' }}>
                      ⚠️ Please enter a playlist name to continue
                    </div>
                  )}
                </div>
              )}

              {/* Append mode: Show member and playlist selection - Hide once both are selected - Only when Append is selected */}
              {isAppending === true && (
                <div style={{ marginTop: '15px' }}>
                  {!selectedMemberIdForAppend || !selectedPlaylistIdForAppend ? (
                    <>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', color: '#b3b3b3', fontSize: '0.9rem' }}>
                          Select Member *
                        </label>
                        <select
                          value={selectedMemberIdForAppend}
                          onChange={(e) => {
                            setSelectedMemberIdForAppend(e.target.value);
                            setSelectedPlaylistIdForAppend('');
                            if (e.target.value) {
                              loadPlaylistsForAppend(e.target.value);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            fontSize: '16px',
                            backgroundColor: '#2a2a2a',
                            color: '#fff',
                            border: '1px solid #555',
                            borderRadius: '4px'
                          }}
                        >
                          <option value="">-- Select a member --</option>
                          {membersForAppend.map((member) => (
                            <option key={member.id} value={member.id} style={{ backgroundColor: '#2a2a2a', color: '#fff' }}>
                              {member.id === user.uid ? '👤 ' : ''}{member.displayName}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedMemberIdForAppend && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', color: '#b3b3b3', fontSize: '0.9rem' }}>
                            Select Playlist *
                          </label>
                          <select
                            value={selectedPlaylistIdForAppend}
                            onChange={(e) => setSelectedPlaylistIdForAppend(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '10px',
                              fontSize: '16px',
                              backgroundColor: '#2a2a2a',
                              color: '#fff',
                              border: '1px solid #555',
                              borderRadius: '4px'
                            }}
                          >
                            <option value="" style={{ backgroundColor: '#2a2a2a', color: '#fff' }}>-- Select a playlist --</option>
                            {availablePlaylistsForAppend.map((playlist) => (
                              <option key={playlist.id} value={playlist.id} style={{ backgroundColor: '#2a2a2a', color: '#fff' }}>
                                {playlist.name} ({playlist.trackCount || 0} songs)
                                {playlist.source === 'roadTrip' ? ' 🚗' : ''}
                                {playlist.isPublic ? ' 🌐' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#2a2a2a', 
                      borderRadius: '6px',
                      border: '1px solid #555'
                    }}>
                      <div style={{ color: '#51cf66', fontSize: '0.9rem', marginBottom: '8px', fontWeight: '600' }}>
                        ✓ Destination Playlist Selected
                      </div>
                      <div style={{ color: '#fff', fontSize: '0.95rem' }}>
                        <strong>{members.find(m => m.id === selectedMemberIdForAppend)?.displayName || 'Member'}</strong>
                        {' → '}
                        <strong>{availablePlaylistsForAppend.find(p => p.id === selectedPlaylistIdForAppend)?.name || 'Playlist'}</strong>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedMemberIdForAppend('');
                          setSelectedPlaylistIdForAppend('');
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '4px 8px',
                          fontSize: '0.85rem',
                          backgroundColor: 'transparent',
                          color: '#888',
                          border: '1px solid #555',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Change
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2: Select Source Playlists - Only show when appending to existing AND both member and playlist are selected */}
            {isAppending === true && selectedMemberIdForAppend && selectedPlaylistIdForAppend && (
              <>
                <div style={{ padding: '1rem', backgroundColor: '#1a1a1a' }}>
                  <h3 style={{ marginBottom: '15px', color: '#fff', fontSize: '1.1rem' }}>
                    Step 2: Select Source Playlists
                    <span style={{ fontSize: '0.9rem', color: '#888', marginLeft: '10px' }}>
                      (Songs will append to "{availablePlaylistsForAppend.find(p => p.id === selectedPlaylistIdForAppend)?.name}")
                    </span>
                    {selectedPlaylistIds.size > 0 && (
                      <span style={{ fontSize: '0.9rem', color: '#51cf66', marginLeft: '10px' }}>
                        ✓ {selectedPlaylistIds.size} playlist{selectedPlaylistIds.size !== 1 ? 's' : ''} selected
                      </span>
                    )}
                  </h3>
                </div>

                <div className="master-playlist-selector-actions">
                  {(() => {
                    // Calculate total playlists
                    const allIds = new Set();
                    playlists.forEach((p) => allIds.add(p.id));
                    Object.values(memberPlaylists).forEach((memberPls) => {
                      memberPls.forEach((p) => allIds.add(p.id));
                    });
                    const totalPlaylists = allIds.size;
                    const allSelected =
                      totalPlaylists > 0 &&
                      selectedPlaylistIds.size === totalPlaylists;

                    return (
                      <button
                        className={`btn-toggle-select-all ${
                          allSelected ? 'deselect' : 'select'
                        }`}
                        onClick={() => {
                          if (allSelected) {
                            setSelectedPlaylistIds(new Set());
                          } else {
                            setSelectedPlaylistIds(allIds);
                          }
                        }}
                      >
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </button>
                    );
                  })()}
                  <div className="selected-count">
                    {selectedPlaylistIds.size} playlist
                    {selectedPlaylistIds.size !== 1 ? 's' : ''} selected
                  </div>
                </div>

                <div className="master-playlist-selector-content">
              {/* My Playlists */}
              <div className="selector-section">
                <div className="my-playlists-selector-group">
                  <div
                    className={`my-playlists-selector-header-btn ${
                      myPlaylistsExpandedInSelector ? 'expanded' : ''
                    }`}
                    onClick={() => {
                      setMyPlaylistsExpandedInSelector(
                        !myPlaylistsExpandedInSelector
                      );
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setMyPlaylistsExpandedInSelector(
                          !myPlaylistsExpandedInSelector
                        );
                      }
                    }}
                  >
                    <div className="my-playlists-header-left">
                      <h3>📋 My Playlists</h3>
                      {playlists.length > 0 && (
                        <span className="my-playlist-count">
                          ({playlists.length})
                        </span>
                      )}
                    </div>
                    <div className="my-playlists-header-right">
                      {playlists.length > 0 && (
                        <div
                          className="section-action-buttons"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {(() => {
                            const myPlaylistIds = new Set(
                              playlists.map((p) => p.id)
                            );
                            const allMySelected = playlists.every((p) =>
                              selectedPlaylistIds.has(p.id)
                            );

                            return (
                              <button
                                className={`btn-section-toggle ${
                                  allMySelected ? 'deselect' : 'select'
                                }`}
                                onClick={() => {
                                  const newSet = new Set(selectedPlaylistIds);
                                  if (allMySelected) {
                                    playlists.forEach((p) =>
                                      newSet.delete(p.id)
                                    );
                                  } else {
                                    playlists.forEach((p) => newSet.add(p.id));
                                  }
                                  setSelectedPlaylistIds(newSet);
                                }}
                              >
                                {allMySelected ? 'Deselect All' : 'Select All'}
                              </button>
                            );
                          })()}
                        </div>
                      )}
                      <span className="accordion-icon">
                        {myPlaylistsExpandedInSelector ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                  {myPlaylistsExpandedInSelector && (
                    <div className="my-playlists-selector-content">
                      {playlists.length === 0 ? (
                        <p className="no-playlists">No playlists yet</p>
                      ) : (
                        playlists.map((playlist) => (
                          <label
                            key={playlist.id}
                            className="playlist-selector-item"
                          >
                            <input
                              type="checkbox"
                              checked={selectedPlaylistIds.has(playlist.id)}
                              onChange={(e) => {
                                const newSet = new Set(selectedPlaylistIds);
                                if (e.target.checked) {
                                  newSet.add(playlist.id);
                                } else {
                                  newSet.delete(playlist.id);
                                }
                                setSelectedPlaylistIds(newSet);
                              }}
                            />
                            <div className="playlist-selector-info">
                              <span className="playlist-selector-name">
                                {playlist.name}
                              </span>
                              <span className="playlist-selector-meta">
                                {playlist.trackCount} songs
                              </span>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Other Members' Playlists */}
              {members.length > 0 && (
                <div className="selector-section">
                  <h3>👥 Other Members' Public Playlists</h3>
                  {members.map((member) => {
                    const isExpanded = expandedMembersInSelector.has(member.id);
                    return (
                      <div key={member.id} className="member-selector-group">
                        <div
                          className={`member-selector-header-btn ${
                            isExpanded ? 'expanded' : ''
                          }`}
                          onClick={() => {
                            const newSet = new Set(expandedMembersInSelector);
                            if (isExpanded) {
                              newSet.delete(member.id);
                            } else {
                              newSet.add(member.id);
                            }
                            setExpandedMembersInSelector(newSet);
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              const newSet = new Set(expandedMembersInSelector);
                              if (isExpanded) {
                                newSet.delete(member.id);
                              } else {
                                newSet.add(member.id);
                              }
                              setExpandedMembersInSelector(newSet);
                            }
                          }}
                        >
                          <div className="member-header-left">
                            {member.photoURL ? (
                              <img
                                src={member.photoURL}
                                alt={member.displayName}
                                className="member-avatar-small"
                              />
                            ) : (
                              <div className="member-avatar-small placeholder">
                                {member.displayName.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <span className="member-selector-name">
                              {member.displayName}
                            </span>
                            {memberPlaylists[member.id] && (
                              <span className="member-playlist-count">
                                ({memberPlaylists[member.id].length})
                              </span>
                            )}
                          </div>
                          <div className="member-header-right">
                            {memberPlaylists[member.id] &&
                              memberPlaylists[member.id].length > 0 && (
                                <div
                                  className="section-select-buttons"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {(() => {
                                    const memberPlIds = new Set(
                                      memberPlaylists[member.id].map(
                                        (p) => p.id
                                      )
                                    );
                                    const allMemberSelected = memberPlaylists[
                                      member.id
                                    ].every((p) =>
                                      selectedPlaylistIds.has(p.id)
                                    );

                                    return (
                                      <button
                                        className={`btn-section-toggle btn-small ${
                                          allMemberSelected
                                            ? 'deselect'
                                            : 'select'
                                        }`}
                                        onClick={() => {
                                          const newSet = new Set(
                                            selectedPlaylistIds
                                          );
                                          if (allMemberSelected) {
                                            memberPlaylists[member.id].forEach(
                                              (p) => newSet.delete(p.id)
                                            );
                                          } else {
                                            memberPlaylists[member.id].forEach(
                                              (p) => newSet.add(p.id)
                                            );
                                          }
                                          setSelectedPlaylistIds(newSet);
                                        }}
                                      >
                                        {allMemberSelected
                                          ? 'Deselect All'
                                          : 'Select All'}
                                      </button>
                                    );
                                  })()}
                                </div>
                              )}
                            <span className="accordion-icon">
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="member-selector-content">
                            {memberPlaylists[member.id] ? (
                              memberPlaylists[member.id].map((playlist) => (
                                <label
                                  key={playlist.id}
                                  className="playlist-selector-item member-playlist-item"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedPlaylistIds.has(
                                      playlist.id
                                    )}
                                    onChange={(e) => {
                                      const newSet = new Set(
                                        selectedPlaylistIds
                                      );
                                      if (e.target.checked) {
                                        newSet.add(playlist.id);
                                      } else {
                                        newSet.delete(playlist.id);
                                      }
                                      setSelectedPlaylistIds(newSet);
                                    }}
                                  />
                                  <div className="playlist-selector-info">
                                    <span className="playlist-selector-name">
                                      {playlist.name}
                                    </span>
                                    <span className="playlist-selector-meta">
                                      {playlist.trackCount} songs
                                    </span>
                                  </div>
                                </label>
                              ))
                            ) : (
                              <p className="loading-playlists">
                                Loading playlists...
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
                </div>
              </>
            )}

            <div className="master-playlist-selector-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  // Allow canceling - user can cancel anytime
                  setShowMasterPlaylistSelector(false);
                  setSelectedPlaylistIds(new Set());
                  setIsAppending(null);
                  setSelectedPlaylistIdForAppend('');
                  setSelectedMemberIdForAppend('');
                  setNewPlaylistName('');
                }}
              >
                Cancel
              </button>
              {isAppending === false ? (
                // Create New mode: Just save the playlist
                <button
                  className="btn-create-master-confirm"
                  onClick={async () => {
                    // Validate playlist name
                    if (!newPlaylistName || !newPlaylistName.trim()) {
                      alert('Please enter a playlist name');
                      return;
                    }
                    // Validate name length
                    if (newPlaylistName.trim().length < 1) {
                      alert('Playlist name must be at least 1 character long');
                      return;
                    }
                    if (newPlaylistName.trim().length > 50) {
                      alert('Playlist name must be 50 characters or less');
                      return;
                    }

                    const playlistName = newPlaylistName.trim();
                    
                    try {
                      const playlistDoc = {
                        name: playlistName,
                        description: '',
                        userId: user.uid,
                        userEmail: user.email,
                        tracks: [],
                        trackCount: 0,
                        isPublic: false,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                      };

                      const docRef = await addDoc(collection(db, 'playlists'), playlistDoc);
                      console.log('✅ Playlist created:', docRef.id);
                      
                      // Load members for append mode (includes current user) if not already loaded
                      // This will auto-select current user and load their playlists
                      await loadMembersForAppend();
                      
                      // Switch to append mode with the newly created playlist
                      setIsAppending(true);
                      setSelectedPlaylistIdForAppend(docRef.id); // Set to newly created playlist
                      
                      // Reload playlists to ensure the newly created one is in the list
                      await loadPlaylistsForAppend(user.uid);
                      
                      console.log('✅ Switched to append mode with newly created playlist');
                    } catch (error) {
                      console.error('❌ Error creating playlist:', error);
                      alert(`Failed to create playlist: ${error.message}`);
                    }
                  }}
                  disabled={
                    isCreatingMaster || !newPlaylistName || !newPlaylistName.trim()
                  }
                >
                  {isCreatingMaster
                    ? '⏳ Creating...'
                    : `💾 Create Playlist "${newPlaylistName || 'New Playlist'}"`}
                </button>
              ) : (
                // Append mode: Start swiping with source playlists
                <button
                  className="btn-create-master-confirm"
                  onClick={async () => {
                    // Validate destination playlist
                    if (!selectedMemberIdForAppend || !selectedPlaylistIdForAppend) {
                      alert('Please select a member and playlist to append to');
                      return;
                    }

                    // Validate source playlists
                    if (selectedPlaylistIds.size === 0) {
                      alert('Please select at least one source playlist');
                      return;
                    }

                    const destinationPlaylistId = selectedPlaylistIdForAppend;

                    // Close modal and start swipe mode with live save enabled
                    setShowMasterPlaylistSelector(false);
                    
                    // Call onCreateMasterPlaylist with destination playlist ID for live saving
                    onCreateMasterPlaylist(Array.from(selectedPlaylistIds), {
                      isAppending: true,
                      appendToPlaylistId: destinationPlaylistId,
                      isLiveSave: true, // Enable live saving
                    });
                    
                    // Reset state
                    setSelectedPlaylistIds(new Set());
                    setIsAppending(null);
                    setSelectedPlaylistIdForAppend('');
                    setSelectedMemberIdForAppend('');
                    setNewPlaylistName('');
                  }}
                  disabled={
                    isCreatingMaster || selectedPlaylistIds.size === 0 ||
                    !selectedMemberIdForAppend || !selectedPlaylistIdForAppend
                  }
                >
                  {isCreatingMaster
                    ? '⏳ Loading Songs...'
                    : `Start Swiping → Append to "${availablePlaylistsForAppend.find(p => p.id === selectedPlaylistIdForAppend)?.name || 'Playlist'}"`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SavedPlaylists;
